import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { portfolioApi, queryClient } from '~/apis';
import {
  MonitoringFilterProtocolResponse,
  PortfolioCopyrightsProtocolsGetCopyrightsRequest,
  PortfolioCopyrightsProtocolsGetDocumentDetailsRequest,
  PortfolioCopyrightsProtocolsGetDocumentsRequest,
  PortfolioDesignProtocolsGetDesignDetailRequest,
  PortfolioDesignProtocolsRequest,
  PortfolioIpDocProtocolsGetBrandDetailRequest,
  PortfolioIpDocProtocolsGetBrandListResponse,
  PortfolioTrademarkProtocolsGetTrademarkDetailRequest,
  PortfolioTrademarkProtocolsGetTrademarkListResponse,
  PortfolioTrademarkProtocolsRequest,
} from '~/swagger/data-contracts';

export const QueryKeyIpDocuments = 'ip_documents';

export const useIpBrandListQuery = <TData = PortfolioIpDocProtocolsGetBrandListResponse>(
  options?: UseQueryOptions<PortfolioIpDocProtocolsGetBrandListResponse, null, TData>
) => {
  return useQuery<PortfolioIpDocProtocolsGetBrandListResponse, null, TData>(
    [QueryKeyIpDocuments, 'list'],
    async () => {
      const { data } = await portfolioApi.brandList();
      return data;
    },
    {
      ...options,
      enabled: options?.enabled === undefined || options?.enabled,
      staleTime: 60000,
    }
  );
};

export const useBrandAddMutation = () => {
  return useMutation(portfolioApi.brandAddCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'list']);
    },
  });
};

export const useBrandUpdateMutation = () => {
  return useMutation(portfolioApi.brandUpdateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'list']);
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'detail']);
    },
  });
};

export const useBrandDeleteMutation = () => {
  return useMutation(portfolioApi.brandDeleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'list']);
    },
  });
};

export const useBrandDetailQuery = (request: PortfolioIpDocProtocolsGetBrandDetailRequest) => {
  return useQuery([QueryKeyIpDocuments, 'detail', request.brand_id], async () => {
    const { data } = await portfolioApi.brandDetailCreate(request);
    return data;
  });
};

// Trademark
export const useIpDocumentsTrademarkQuery = <TData = PortfolioTrademarkProtocolsGetTrademarkListResponse>(
  request: PortfolioTrademarkProtocolsRequest,
  options?: UseQueryOptions<PortfolioTrademarkProtocolsGetTrademarkListResponse, unknown, TData>
) => {
  return useQuery<PortfolioTrademarkProtocolsGetTrademarkListResponse, unknown, TData>(
    [QueryKeyIpDocuments, 'trademark', request],
    async () => {
      const { data } = await portfolioApi.trademarkCreate(request);
      return data;
    },
    options
  );
};

export const useIpDocumentsTrademarkAddMutation = () => {
  return useMutation(portfolioApi.trademarkAddCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'trademark']);
    },
  });
};

export const useIpDocumentsTrademarkDetailQuery = (
  request: PortfolioTrademarkProtocolsGetTrademarkDetailRequest,
  enabled: boolean
) => {
  return useQuery(
    [QueryKeyIpDocuments, 'trademark', 'detail', request.doc_id],
    async () => {
      const { data } = await portfolioApi.trademarkDetailCreate(request);
      return data;
    },
    { enabled }
  );
};

export const useIpDocumentsTrademarkUpdateMutation = () => {
  return useMutation(portfolioApi.trademarkUpdateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'trademark']);
    },
  });
};

export const useIpDocumentsTrademarkDeleteMutation = () => {
  return useMutation(portfolioApi.trademarkDeleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'trademark']);
    },
  });
};

// Copyright
export const useCopyrightsDocumentListQuery = (request: PortfolioCopyrightsProtocolsGetDocumentsRequest) => {
  return useQuery([QueryKeyIpDocuments, 'copyrights', 'document', request.brand_id], async () => {
    const { data } = await portfolioApi.copyrightsDocListCreate(request);
    return data;
  });
};

export const useCopyrightsDocumentDetailQuery = (
  request: PortfolioCopyrightsProtocolsGetDocumentDetailsRequest,
  enabled: boolean
) => {
  return useQuery(
    [QueryKeyIpDocuments, 'copyrights', 'document', 'detail', request.id],
    async () => {
      const { data } = await portfolioApi.copyrightsDocDetailsCreate(request);
      return data;
    },
    { enabled }
  );
};

export const useCopyrightsDocumentAddMutation = () => {
  return useMutation(portfolioApi.copyrightsDocAddCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'copyrights', 'document']);
    },
  });
};

export const useCopyrightsDocumentUpdateMutation = () => {
  return useMutation(portfolioApi.copyrightsDocUpdateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'copyrights', 'document']);
    },
  });
};

export const useCopyrightsDocumentDeleteMutation = () => {
  return useMutation(portfolioApi.copyrightsDocDeleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'copyrights', 'document']);
    },
  });
};

export const useCopyrightsCopyrightListQuery = (
  request: PortfolioCopyrightsProtocolsGetCopyrightsRequest,
  enabled: boolean
) => {
  return useQuery(
    [QueryKeyIpDocuments, 'copyrights', 'copyright', request.doc_id, request.sorted_order_enum],
    async () => {
      const { data } = await portfolioApi.copyrightsCopyrightListCreate(request);
      return data;
    },
    { enabled }
  );
};

export const useCopyrightsCopyrightAddMutation = () => {
  return useMutation(portfolioApi.copyrightsCopyrightAddCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'copyrights', 'copyright']);
    },
  });
};

export const useCopyrightsCopyrightUpdateMutation = () => {
  return useMutation(portfolioApi.copyrightsCopyrightUpdateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'copyrights', 'copyright']);
    },
  });
};

export const useCopyrightsCopyrightDeleteMutation = () => {
  return useMutation(portfolioApi.copyrightsCopyrightDeleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'copyrights', 'copyright']);
    },
  });
};

// Design
export const useIpDocumentsDesignQuery = (request: PortfolioDesignProtocolsRequest) => {
  return useQuery([QueryKeyIpDocuments, 'design', request], async () => {
    const { data } = await portfolioApi.designCreate(request);
    return data;
  });
};

export const useIpDocumentsDesignAddMutation = () => {
  return useMutation(portfolioApi.designAddCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'design']);
    },
  });
};

export const useIpDocumentsDesignUpdateMutation = () => {
  return useMutation(portfolioApi.designUpdateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'design']);
    },
  });
};

export const useIpDocumentsDesignDeleteMutation = () => {
  return useMutation(portfolioApi.designDeleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyIpDocuments, 'design']);
    },
  });
};

export const useIpDocumentsDesignDetailQuery = (
  request: PortfolioDesignProtocolsGetDesignDetailRequest,
  enabled: boolean
) => {
  return useQuery(
    [QueryKeyIpDocuments, 'design', 'detail', request.doc_id],
    async () => {
      const { data } = await portfolioApi.designDetailCreate(request);
      return data;
    },
    { enabled }
  );
};

// Other
export const useIpDocumentsOwnerListQuery = () => {
  return useQuery([QueryKeyIpDocuments, 'owner_list'], async () => {
    const { data } = await portfolioApi.ownerList();
    return data;
  });
};

export const useIpDocumentsLicenseeListQuery = () => {
  return useQuery([QueryKeyIpDocuments, 'licensee_list'], async () => {
    const { data } = await portfolioApi.licenseeList();
    return data;
  });
};

export const useIpDocumentFileUploadMutation = () => {
  return useMutation(portfolioApi.presignedUploadCreate);
};

export const useIpDocumentFileDownloadMutation = () => {
  return useMutation(portfolioApi.presignedDownloadCreate);
};
export const useTrademarkGetAllCountriesQuery = <TData = MonitoringFilterProtocolResponse>(
  request: PortfolioTrademarkProtocolsRequest,
  options?: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>(
    [QueryKeyIpDocuments, 'get-all-trademark-countries', request],
    async () => {
      const { data } = await portfolioApi.trademarkGetAllCountriesCreate(request);

      return data;
    },
    options
  );
};
export const useDesignGetAllCountriesQuery = <TData = MonitoringFilterProtocolResponse>(
  request: PortfolioTrademarkProtocolsRequest,
  options?: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>(
    [QueryKeyIpDocuments, 'get-all-design-countries', request],
    async () => {
      const { data } = await portfolioApi.designGetAllCountriesCreate(request);

      return data;
    },
    options
  );
};

export const useTrademarkGetAllClassesQuery = <TData = MonitoringFilterProtocolResponse>(
  request: PortfolioTrademarkProtocolsRequest,
  options?: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>(
    [QueryKeyIpDocuments, 'get-all-classes', request],
    async () => {
      const { data } = await portfolioApi.trademarkGetAllClassesCreate(request);

      return data;
    },
    options
  );
};
